import AddCoverageCard from '@root/quotes/src/components/coverage-options/add-coverage-card';
import Coverage from '@root/auto-pricing/src/models/coverage';
import CoverageOptionsToggle from '@root/quotes/src/components/coverage/coverage-option-toggle';
import CoverageSelectionCard from '@root/quotes/src/components/coverage-options/coverage-selection-card';
import Money from '@root/core/src/models/money';
import NjExtraPipLearnMoreModal from '@root/quotes/src/components/modals/nj-extra-pip-learn-more-modal';
import PropTypes from '@root/vendor/prop-types';
import QuotesSelect from '@root/quotes/src/components/form-elements/quotes-select';
import React from '@root/vendor/react';
import { Colors, StyleSheet, Theme } from '@root/core/src/utils/styles';
import { extraPipLabelKey, findPipPeer, getExtraPipInfo, hasExtraPip, pipPeerMatches } from '@root/quotes/src/utils/pip-nj-filtering';

const SYMBOL = Coverage.Symbols.PERSONAL_INJURY_PROTECTION;

// Server must include per_day in pip short_string for all 17 non-reject options to be distinct.
// perDay = weekly income continuation amount (despite the field name).
function extraPipLabel(selection) {
  if (!hasExtraPip(selection)) { return null; }
  const weekly = Money.fromDollars(selection.perDay).formattedDollars();
  const total = selection.perOccurrence !== null && selection.perOccurrence !== undefined
    ? `${Money.fromDollars(selection.perOccurrence).formattedDollars()} Total`
    : 'Unlimited Total';
  return `${weekly} Week / ${total}`;
}

function getExtraPipOptions(coverageSelections, coverage) {
  const BASE_ORDER = 0;
  const seen = new Set();

  return coverageSelections.pip
    .filter((selection) => {
      if (!hasExtraPip(selection)) { return false; }
      const key = extraPipLabelKey(selection);
      if (seen.has(key)) { return false; }
      const peers = coverageSelections.pip.filter((s) => extraPipLabelKey(s) === key);
      const preferred = findPipPeer(peers, coverage, ['perDay', 'perOccurrence'], ['residentRelativesIncluded']) ?? peers[0];
      if (selection !== preferred) { return false; }
      seen.add(key);
      return true;
    })
    .map((selection) => {
      const key = extraPipLabelKey(selection);
      let sortOrder;
      if (key === '100:5200') {
        sortOrder = BASE_ORDER;
      } else {
        const isUnlimited = selection.perOccurrence === null || selection.perOccurrence === undefined;
        sortOrder = (isUnlimited ? 100 : 10) + (selection.perDay ?? 0) / 100;
      }
      return {
        label: extraPipLabel(selection),
        subLabel: selection.value === coverage.id ? 'Selected' : selection.subLabel,
        value: selection.value,
        _sortOrder: sortOrder,
      };
    })
    .sort((a, b) => a._sortOrder - b._sortOrder)
    .map((option) => {
      const result = { ...option };
      delete result._sortOrder;
      return result;
    });
}

export default function ExtraPipNjCard({
  cardHeaderBackgroundColor,
  coverage,
  coverageSelections,
  dropdownIndicator,
  infoBrandColor,
  mobileSelectedOptionStyles,
  onCoverageChange,
  onCoverageSelectClose,
  onCoverageSelectOpen,
  optionStyles,
  primaryBrandColor,
  secondaryBrandColor,
  selectBoxFocusedStyles,
  selectBoxStyles,
}) {
  const extraPipOptions = getExtraPipOptions(coverageSelections, coverage);
  const selectedExtraPipValue = extraPipOptions.find((o) => {
    const sel = coverageSelections.pip.find((s) => s.value === o.value);
    return sel && extraPipLabelKey(sel) === extraPipLabelKey(coverage);
  })?.value ?? extraPipOptions[0]?.value ?? coverage.id;

  const coverageKey = extraPipLabelKey(coverage);
  const isRejected = coverageKey === 'null:null';
  const isBase = coverageKey === '100:5200';

  const rejectPeers = coverageSelections.pip.filter((s) => extraPipLabelKey(s) === 'null:null');
  const rejectCoverageId = findPipPeer(rejectPeers, coverage, ['perDay', 'perOccurrence'], ['residentRelativesIncluded'])?.value;
  const basePeers = coverageSelections.pip.filter((s) => extraPipLabelKey(s) === '100:5200');
  const baseCoverageId = findPipPeer(basePeers, coverage, ['perDay', 'perOccurrence'])?.value;

  const pipInfo = getExtraPipInfo(coverage);

  return (
    <>
      <AddCoverageCard
        description={'Covers non-medical expenses incurred regardless of fault.'}
        headerBackgroundColor={cardHeaderBackgroundColor}
        heading={'Non-Medical Expenses'}
        isCoverageAdded={!isRejected}
        onAddCoverage={() => baseCoverageId && onCoverageChange(baseCoverageId, SYMBOL)}
        primaryBrandColor={primaryBrandColor}
      >
        <NjExtraPipLearnMoreModal
          infoBrandColor={infoBrandColor}
          primaryBrandColor={primaryBrandColor}
        />
      </AddCoverageCard>
      <CoverageSelectionCard
        description={'Covers non-medical expenses incurred regardless of fault.'}
        headerBackgroundColor={cardHeaderBackgroundColor}
        heading={'Non-Medical Expenses'}
        isCoverageAdded={!isRejected}
        onRemoveCoverage={rejectCoverageId ? () => onCoverageChange(rejectCoverageId, SYMBOL) : null}
        renderLearnMore={() => (
          <NjExtraPipLearnMoreModal
            infoBrandColor={infoBrandColor}
            primaryBrandColor={primaryBrandColor}
          />
        )}
        vehiclesSelected={true}
      >
        <QuotesSelect
          dropdownIndicator={dropdownIndicator}
          dropdownIndicatorColor={secondaryBrandColor}
          inputId={'pip-extra-pip-coverage-select'}
          inputName={'pip-extra-pip-coverage-select'}
          label={'Select income continuation coverage'}
          mobileSelectedOptionStyles={mobileSelectedOptionStyles}
          onChange={(newValue) => onCoverageChange(newValue, SYMBOL)}
          onClose={onCoverageSelectClose}
          onOpen={onCoverageSelectOpen}
          options={extraPipOptions}
          optionStyles={optionStyles}
          placeholder={'Select income continuation coverage'}
          selectBoxFocusedStyles={selectBoxFocusedStyles}
          selectBoxStyles={selectBoxStyles}
          selectedValue={selectedExtraPipValue}
        >
          {pipInfo && (
            <div css={styles.infoBox}>
              <div css={styles.infoRow}>
                <div css={styles.infoRowLeft}>
                  <span css={styles.infoLabel}>{'Essential service'}</span>
                  <span css={styles.infoValue}>{pipInfo.essentialService}</span>
                </div>
                <span css={styles.infoIncluded}>{'Included'}</span>
              </div>
              <div css={styles.infoRow}>
                <div css={styles.infoRowLeft}>
                  <span css={styles.infoLabel}>{'Death benefit'}</span>
                  <span css={styles.infoValue}>{pipInfo.deathBenefit}</span>
                </div>
                <span css={styles.infoIncluded}>{'Included'}</span>
              </div>
              <div css={styles.infoRow}>
                <div css={styles.infoRowLeft}>
                  <span css={styles.infoLabel}>{'Funeral expenses'}</span>
                  <span css={styles.infoValue}>{pipInfo.funeralExpenses}</span>
                </div>
                <span css={styles.infoIncluded}>{'Included'}</span>
              </div>
              {isBase &&
                <p css={styles.infoFootnote}>{'Amounts are dependent on income continuation selection.'}</p>
              }
            </div>
          )}
          {!isRejected && !isBase && (
            <CoverageOptionsToggle
              containerStyleOverrides={styles.toggleContainer}
              description={'Apply these selections to dependents living with you?'}
              isSelected={coverage.coverageOptions.residentRelativesIncluded === Coverage.CoverageOptionChoice.YES}
              onToggleChange={() => {
                const newNis = coverage.coverageOptions.residentRelativesIncluded === Coverage.CoverageOptionChoice.YES
                  ? Coverage.CoverageOptionChoice.NO
                  : Coverage.CoverageOptionChoice.YES;
                const nisPeers = coverageSelections.pip.filter((s) => extraPipLabelKey(s) === coverageKey);
                const flipped = nisPeers.find((s) => pipPeerMatches(s, {
                  ...coverage,
                  coverageOptions: {
                    ...coverage.coverageOptions,
                    residentRelativesIncluded: newNis,
                  },
                }, []));
                if (flipped) { onCoverageChange(flipped.value, SYMBOL); }
              }}
              toggleText={coverage.coverageOptions.residentRelativesIncluded === Coverage.CoverageOptionChoice.YES ? 'Yes' : 'No'}
            />
          )}
        </QuotesSelect>
      </CoverageSelectionCard>
    </>
  );
}

ExtraPipNjCard.propTypes = {
  cardHeaderBackgroundColor: PropTypes.string,
  coverage: PropTypes.object.isRequired,
  coverageSelections: PropTypes.object.isRequired,
  dropdownIndicator: PropTypes.string,
  infoBrandColor: PropTypes.string,
  mobileSelectedOptionStyles: PropTypes.object,
  onCoverageChange: PropTypes.func.isRequired,
  onCoverageSelectClose: PropTypes.func.isRequired,
  onCoverageSelectOpen: PropTypes.func.isRequired,
  optionStyles: PropTypes.object,
  primaryBrandColor: PropTypes.string,
  secondaryBrandColor: PropTypes.string,
  selectBoxFocusedStyles: PropTypes.object,
  selectBoxStyles: PropTypes.object,
};

const styles = StyleSheet.create({
  infoBox: {
    background: Colors.nearWhite(),
    border: `1px solid ${Colors.gray30()}`,
    ...Theme.roundedCorners(),
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 8,
    padding: 12,
  },
  infoRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  infoRowLeft: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  infoLabel: {
    ...Theme.paragraph2(),
    color: Colors.gray50(),
  },
  infoValue: {
    ...Theme.paragraph1(),
    color: Colors.nearBlack(),
  },
  infoIncluded: {
    ...Theme.paragraph1(),
    color: Colors.gray60(),
    flexShrink: 0,
    marginLeft: 8,
    whiteSpace: 'nowrap',
  },
  infoFootnote: {
    ...Theme.paragraph2(),
    color: Colors.gray60(),
    margin: 0,
  },
  toggleContainer: {
    alignItems: 'flex-start',
    marginTop: 24,
    '& > div': {
      alignItems: 'center',
    },
  },
});
